import { RuleActionType } from '@jupiterone/jupiter-types';
import {
  ArrayInput,
  IntegrationInstanceSelector,
  JupiterTheme,
} from '@jupiterone/jupiter-web-toolkit';
import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { ChangeEvent, useEffect, useState } from 'react';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { RuleActionData, SlackActionData } from 'jupiter-web-alerts/src/types';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    full: {
      display: 'block',
      marginBottom: theme.spacing(1),
    },
    link: { textDecoration: 'none', color: theme.palette.primary.main },
  })
);

interface Props {
  onUpdate: (newValue: RuleActionData) => void;
  disabled?: boolean;
  initialActionData?: SlackActionData;
  templateData?: Record<string, string>;
}

const DEFAULT_SLACK_BODY = `*Affected Items:* \n\n- {{queries.query0.data|mapProperty('displayName')|join('\n- ')}}`;
const TEMPLATED_SLACK_BODY = `*Affected Items:* \n\n{{queries.query0.data|mapTemplate('slackBody')|join(' ')}}`;

const SlackRuleConfig = ({
  onUpdate,
  disabled,
  templateData,
  initialActionData,
}: Props) => {
  const classes = useStyles();
  const WEBHOOK_HELP_TEXT = (
    <>
      Slack webhooks are now deprecated, see the docs{' '}
      <a
        href="https://support.jupiterone.io/hc/en-us/articles/360046977154-Slack"
        target="_blank"
        rel="noreferrer"
        className={classes.link}
      >
        here
      </a>{' '}
      for details on how to setup the JupiterOne Slack App.
    </>
  );
  const DEFAULT_ACTION_DATA = {
    type: RuleActionType.SEND_SLACK_MESSAGE,
    body: templateData?.slackBody ? TEMPLATED_SLACK_BODY : DEFAULT_SLACK_BODY,
  };

  const [actionValues, setActionValues] = useState<SlackActionData>(
    initialActionData || DEFAULT_ACTION_DATA
  );

  useEffect(() => {
    const values = { ...actionValues };
    if (values.integrationInstanceId) {
      values.webhookUrl = undefined;
    } else {
      values.integrationInstanceId = undefined;
      values.channels = undefined;
    }
    onUpdate(values);
  }, [actionValues]);

  const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) =>
    setActionValues({
      ...actionValues,
      [e.target.name]: e.target.value,
    });

  return (
    <div>
      <IntegrationInstanceSelector
        disabled={disabled}
        value={actionValues.integrationInstanceId}
        integrationFilter="slack"
        onSelect={(id: string) =>
          handleChangeValue({
            target: { name: 'integrationInstanceId', value: id },
          } as any)
        }
      />

      <ArrayInput
        inputLabel="Channels"
        disabled={disabled}
        initialValues={initialActionData?.channels}
        onUpdate={(values) =>
          handleChangeValue({
            target: { name: 'channels', value: values },
          } as any)
        }
      />

      {initialActionData?.webhookUrl && (
        <TextField
          className={classes.full}
          fullWidth={true}
          onChange={handleChangeValue}
          name={'webhookUrl'}
          value={actionValues.webhookUrl}
          label={'Webhook Url (deprecated)'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={WEBHOOK_HELP_TEXT} interactive>
                  <IconButton>
                    <HelpIcon size={20} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          disabled={disabled}
        />
      )}

      <TextField
        className={classes.full}
        fullWidth={true}
        onChange={handleChangeValue}
        name={'body'}
        multiline
        rowsMax={5}
        value={actionValues.body}
        label={'Slack Message Body'}
        disabled={disabled}
      />
    </div>
  );
};

export default SlackRuleConfig;
